import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Search } from "lucide-react";
import SearchModal from "@/components/SearchModal";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { OrnamentDivider } from "@/components/Ornaments";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  // Hash-länkar (t.ex. "Bygdepuls" → /#bygdepuls) måste rulla även när vi redan är
  // på samma URL/hash – useEffect i Home triggas inte om hashen är oförändrad.
  // Därför fångar vi klick direkt här och scrollar manuellt när vi redan står
  // på målsidan. Vid sidbyte (t.ex. från /foretag) sker normal SPA-navigation
  // och Home:s scroll-handler tar över.
  const handleNavClick = (item) => (e) => {
    if (!item.to.includes("#")) return;
    const [path, hash] = item.to.split("#");
    if (location.pathname !== (path || "/")) return; // ej samma sida → låt routern hantera
    const el = document.getElementById(hash);
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const NAV_ITEMS = [
    { to: "/", label: t("nav.start") },
    { to: "/#bygdepuls", label: "Bygdepuls" },
    { to: "/anslagstavla", label: t("nav.anslagstavla") },
    { to: "/evenemang", label: t("nav.evenemang") },
    { to: "/foreningar-evenemang", label: t("nav.foreningar") },
    { to: "/foretag", label: t("nav.foretag") },
    { to: "/bostader", label: t("nav.bostader") },
    { to: "/praktiskt", label: t("nav.praktiskt") },
    // { to: "/friluftsliv", label: t("nav.friluftsliv") }, // tillfälligt dolt — innehållet nås via /kartor (build 88)
    { to: "/kronika", label: t("nav.kronika") },
    { to: "/bilder", label: t("nav.utstallning") },
    { to: "/kartor", label: t("nav.karta") },
    { to: "/om-oss", label: t("nav.om_oss") },
  ];

  return (
    <>
      <header data-testid="header" className="sticky top-0 z-50 bg-bone/90 backdrop-blur-md border-b border-warm-border">
        {/* Band 1 – Huvudnamn (dominerar, Fraktur, ca 62% av höjden) */}
        <div className="bg-bone/60 border-b border-warm-border/50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
            <Link
              to="/"
              data-testid="logo-link"
              className="no-underline flex-1 min-w-0 text-center sm:text-left"
            >
              <p
                data-testid="header-valkommen"
                className="font-body text-[10px] sm:text-[11px] italic text-bark/55 tracking-[0.15em] mb-0.5 sm:mb-1"
              >
                {t("common.valkommen_hit")}
              </p>
              <h1
                data-testid="header-ortszeile"
                translate="no"
                className="notranslate font-fraktur-smooth text-falu text-[15px] xs:text-base sm:text-2xl lg:text-3xl leading-[1.15] tracking-wide"
              >
                <span className="inline sm:whitespace-nowrap">Backe, Rossön och bygderna</span>
                <span className="hidden sm:inline"> &ndash; </span>
                <span className="block sm:inline text-[13px] xs:text-sm sm:text-2xl lg:text-3xl">.live vid Fjällsjöälven.</span>
              </h1>
              {/* Kort budskap direkt under huvudrubriken, före menyn.
                  Build 96.19 — samma typografi som "Välkommen hit"-labeln ovanför h1
                  (font-body · italic · text-bark/55 · tracking-[0.15em] · text-[10px]/[11px]). */}
              <p
                data-testid="header-budskap"
                translate="no"
                className="notranslate font-body text-[10px] sm:text-[11px] italic text-bark/55 tracking-[0.15em] mt-0.5 sm:mt-1"
              >
                Din blick på bygden hör hemma här
              </p>
            </Link>

            <Link
              to="/skicka-anslag"
              data-testid="cta-skicka-anslag"
              className="hidden sm:inline-flex items-center bg-falu hover:bg-falu-hover text-parchment font-body font-semibold text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5 rounded-sm transition-colors no-underline whitespace-nowrap shrink-0"
            >
              {t("nav.skicka_anslag")}
            </Link>

            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 text-ink shrink-0"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? "Stäng meny" : "Öppna meny"}
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>

            {/* Mobile language switcher – alltid tillgänglig, även med menyn stängd */}
            <div className="lg:hidden shrink-0">
              <LanguageSwitcher compact />
            </div>
          </div>
          <OrnamentDivider className="text-gold-deep opacity-45 -mt-0.5 pb-1 hidden sm:flex" />
        </div>

        {/* Band 2 – Navigation (dezent, ca 38%) */}
        <div className="hidden lg:block">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between py-1.5">
              <nav data-testid="desktop-nav" className="flex items-center gap-1 flex-wrap">
                {NAV_ITEMS.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={handleNavClick(item)}
                    data-testid={`nav-${item.to.replace(/[/#]/g, "") || "start"}`}
                    className={`px-2.5 py-1.5 text-[13px] font-body font-medium transition-colors no-underline rounded-sm whitespace-nowrap tracking-wide ${
                      location.pathname === item.to
                        ? "text-falu bg-falu/5"
                        : "text-bark/80 hover:text-falu"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
              <div className="flex items-center gap-1 shrink-0">
                <LanguageSwitcher compact />
                <button
                  data-testid="search-button"
                  onClick={() => setSearchOpen(true)}
                  className="p-1.5 text-bark/70 hover:text-falu transition-colors"
                  aria-label="Sök"
                >
                  <Search size={16} />
                </button>
                <span className="text-bark/20 mx-1">|</span>
                <Link
                  to="/mod/login"
                  data-testid="nav-moderator-login"
                  className="px-2 py-1 text-[11px] font-body font-medium text-bark/50 hover:text-falu no-underline tracking-wider uppercase"
                >
                  {t("nav.moderator")}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile meny */}
        {menuOpen && (
          <div data-testid="mobile-menu" className="lg:hidden border-t border-warm-border bg-bone px-4 pb-4">
            <nav className="flex flex-col gap-0.5 pt-2">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={(e) => { handleNavClick(item)(e); setMenuOpen(false); }}
                  className={`px-3 py-3 text-base font-body font-medium rounded-sm no-underline transition-colors ${
                    location.pathname === item.to
                      ? "text-falu bg-bone-alt"
                      : "text-ink hover:bg-bone-alt"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <div className="border-t border-warm-border/50 mt-2 pt-2 flex items-center justify-between">
                <button
                  data-testid="search-button-mobile"
                  onClick={() => { setSearchOpen(true); setMenuOpen(false); }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-bark hover:text-falu"
                >
                  <Search size={16} /> Sök
                </button>
                <Link
                  to="/mod/login"
                  onClick={() => setMenuOpen(false)}
                  className="px-3 py-2 text-xs text-bark/60 hover:text-falu no-underline uppercase tracking-wider"
                >
                  Moderator-login
                </Link>
              </div>
              <Link
                to="/skicka-anslag"
                onClick={() => setMenuOpen(false)}
                className="mt-3 text-center bg-falu hover:bg-falu-hover text-parchment font-body font-semibold text-base px-5 py-3.5 rounded-sm transition-colors no-underline"
              >
                {t("nav.skicka_anslag")}
              </Link>
              <div className="mt-3 pt-3 border-t border-warm-border/50 flex justify-center">
                <LanguageSwitcher />
              </div>
            </nav>
          </div>
        )}
      </header>

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
